import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2, Calendar, Users, MapPin, Phone as PhoneIcon, User, Mail as MailIcon, MessageSquare, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useQuote } from '../context/QuoteContext';
import { CITIES, OCCASIONS, PLANS } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  phone: '',
  email: '',
  event_type: '',
  city: '',
  event_date: '',
  guest_count: '',
  plan_interest: '',
  message: '',
};

const EnquiryModal = () => {
  const { isOpen, prefill, closeQuote } = useQuote();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({ ...emptyForm, ...(prefill || {}) });
      setError('');
      setDone(false);
    }
  }, [isOpen, prefill]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && isOpen && closeQuote();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeQuote]);

  if (!isOpen) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.phone.trim() || !form.event_type || !form.city) {
      setError('Please fill Name, Phone, Event Type and City.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        event_type: form.event_type,
        city: form.city,
        event_date: form.event_date || null,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
        plan_interest: form.plan_interest || null,
        message: form.message.trim() || null,
        source: prefill?.source || 'website',
      };
      await axios.post(`${API}/enquiries`, payload);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(' · ')
          : 'Could not submit your enquiry. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={closeQuote}
      data-testid="enquiry-modal-backdrop"
    >
      <div
        className="relative w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="enquiry-modal"
      >
        {/* Header ribbon */}
        <div className="sticky top-0 z-10 flex items-center justify-between bg-pink-brand text-white px-6 py-4 rounded-t-3xl">
          <div>
            <div className="font-script text-sm text-white/95">Let's plan your event</div>
            <div className="font-display font-bold text-xl leading-tight">Request a Quote</div>
          </div>
          <button
            onClick={closeQuote}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
            data-testid="enquiry-modal-close-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 sm:p-12 text-center" data-testid="enquiry-modal-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-blush text-pink-brand flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="font-display font-bold text-2xl text-stone-900">Thank you, {form.name.split(' ')[0]}!</h3>
            <p className="mt-2 text-stone-600 max-w-md mx-auto">
              Your enquiry has landed. A Coco-consultant will reach out on <span className="font-semibold text-stone-900">{form.phone}</span> within 24 hours with a personalised quote.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-pink-brand font-script text-lg">
              <Sparkles className="w-4 h-4" /> Sip · Smile · Celebrate
            </div>
            <div className="mt-8">
              <Button
                onClick={closeQuote}
                className="rounded-full bg-pink-brand hover:bg-[#BE185D] text-white px-8"
                data-testid="enquiry-modal-close-success-button"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-8 grid gap-5">
            <p className="text-sm text-gray-600 -mt-1">
              Tell us a little about your event — we'll come back with a plan and a price within a day.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="enq-name" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <User className="w-3 h-3 inline mr-1" /> Full Name *
                </Label>
                <Input
                  id="enq-name"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Your name"
                  className="mt-1"
                  data-testid="enquiry-name-input"
                />
              </div>
              <div>
                <Label htmlFor="enq-phone" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <PhoneIcon className="w-3 h-3 inline mr-1" /> Phone *
                </Label>
                <Input
                  id="enq-phone"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  placeholder="+91 98xxxxxxxx"
                  className="mt-1"
                  data-testid="enquiry-phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="enq-email" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                <MailIcon className="w-3 h-3 inline mr-1" /> Email (optional)
              </Label>
              <Input
                id="enq-email"
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="you@example.com"
                className="mt-1"
                data-testid="enquiry-email-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <Sparkles className="w-3 h-3 inline mr-1" /> Event Type *
                </Label>
                <Select value={form.event_type} onValueChange={(v) => setField('event_type', v)}>
                  <SelectTrigger className="mt-1" data-testid="enquiry-event-type-trigger">
                    <SelectValue placeholder="Choose an occasion" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {OCCASIONS.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`enquiry-event-type-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <MapPin className="w-3 h-3 inline mr-1" /> City *
                </Label>
                <Select value={form.city} onValueChange={(v) => setField('city', v)}>
                  <SelectTrigger className="mt-1" data-testid="enquiry-city-trigger">
                    <SelectValue placeholder="Choose a city" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {CITIES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`enquiry-city-option-${c.toLowerCase()}`}>{c}</SelectItem>
                    ))}
                    <SelectItem value="Other" data-testid="enquiry-city-option-other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="enq-date" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <Calendar className="w-3 h-3 inline mr-1" /> Event Date
                </Label>
                <Input
                  id="enq-date"
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setField('event_date', e.target.value)}
                  className="mt-1"
                  data-testid="enquiry-date-input"
                />
              </div>
              <div>
                <Label htmlFor="enq-guests" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <Users className="w-3 h-3 inline mr-1" /> Guest Count
                </Label>
                <Input
                  id="enq-guests"
                  type="number"
                  min="1"
                  value={form.guest_count}
                  onChange={(e) => setField('guest_count', e.target.value)}
                  placeholder="e.g. 250"
                  className="mt-1"
                  data-testid="enquiry-guests-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Preferred Plan</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PLANS.map((p) => {
                  const active = form.plan_interest === p.name;
                  return (
                    <button
                      type="button"
                      key={p.name}
                      onClick={() => setField('plan_interest', active ? '' : p.name)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        active
                          ? 'bg-pink-brand text-white border-pink-brand shadow-md'
                          : 'bg-white text-stone-700 border-stone-200 hover:border-pink-brand'
                      }`}
                      data-testid={`enquiry-plan-${p.name.toLowerCase().replace(/\s+/g, '-')}-chip`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="enq-msg" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                <MessageSquare className="w-3 h-3 inline mr-1" /> Anything else?
              </Label>
              <Textarea
                id="enq-msg"
                value={form.message}
                onChange={(e) => setField('message', e.target.value)}
                placeholder="Custom monogram, dietary notes, timing, venue..."
                rows={3}
                className="mt-1"
                data-testid="enquiry-message-input"
              />
            </div>

            {error && (
              <div
                className="rounded-lg bg-red-50 text-red-700 text-sm px-4 py-2 border border-red-200"
                data-testid="enquiry-modal-error"
              >
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <p className="text-[11px] text-gray-500 max-w-xs">
                By submitting, you agree to be contacted by our team. We'll never spam you.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-pink-brand hover:bg-[#BE185D] text-white px-8 py-6 text-base font-semibold shadow-pink-glow disabled:opacity-70"
                data-testid="enquiry-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                  </>
                ) : (
                  <>Get My Custom Quote</>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EnquiryModal;
